import { z } from 'zod'
import type { ResourceHome } from './apps'
import { docActions, docFields, type Submittable, type SubmittableConfig } from './docstatus'
import { type SubjectInfo, standardActions } from './permissions'

/**
 * A resource is one business entity (shipment, customer, invoice…).
 * Its definition is written once in `@tnn/domain` and drives:
 *   - validation on the API and in forms (the Zod schema)
 *   - list columns, filters, search and the mobile card
 *   - form layout and field widgets
 *   - import column mapping and export columns
 *   - the permission matrix (subject + actions)
 *
 * Field types are inferred from the Zod schema; `fields` only needs the
 * parts that cannot be inferred (labels come from i18n by convention).
 */

export type FieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'integer'
  | 'money'
  | 'percent'
  | 'date'
  | 'datetime'
  | 'time'
  | 'select'
  | 'multiselect'
  | 'boolean'
  | 'ref'
  | 'suggest'
  | 'tags'
  | 'email'
  | 'phone'
  | 'taxId'
  | 'password'
  | 'json'
  /** An uploaded file (slip, photo, PDF): the value is its id from `POST /api/files` */
  | 'file'

/** Semantic colour of a status value; the UI maps tones to palette roles. */
export type Tone = 'neutral' | 'info' | 'progress' | 'success' | 'warning' | 'danger'

/**
 * A colour a record wears as its own (a kind of leave), not a status: each a light and dark pair like a tone
 * (`--tnn-hue-<hue>-bg`, `-fg`, `-dot`)
 */
export const hues = ['red', 'orange', 'amber', 'lime', 'green', 'teal', 'cyan', 'blue', 'indigo', 'violet', 'pink', 'grey'] as const
export type Hue = (typeof hues)[number]

export interface ColumnMeta {
  width?: number
  align?: 'start' | 'center' | 'end'
  /** 1 = always (tablets too), 2 = desktop (62em+), 3 = wide screens (75em+). Phones show cards. */
  priority?: 1 | 2 | 3
  sortable?: boolean
  /** Hidden by default; users can switch it on from the column menu */
  hidden?: boolean
}

export interface RefMeta {
  /** Target resource key, e.g. `tms.vehicle` */
  resource: string
  /** Only offer records matching these filters when choosing, e.g. `{ status: 'active' }` */
  where?: Record<string, string>
}

export interface FormFieldMeta {
  /** Width on a 12-column grid at desktop size; phones always use the full width */
  span?: 3 | 4 | 6 | 8 | 12
  section?: string
  /** The app renders this field itself (e.g. invoice lines) */
  custom?: boolean
  /** Filled by another input on the form (an address picker sets district and postcode): sent, not drawn */
  hidden?: boolean
  placeholder?: string
}

/** How a spreadsheet column fills a field (imports) */
export interface FieldImport {
  /** Other headers that mean this field (`รหัสลูกค้า` for the code) */
  aliases?: readonly string[]
  /** The template's header (an i18n key) when the label is the form's, not the column's: the area picker's ตำบล อำเภอ จังหวัด is only the province in a sheet */
  header?: string
  /** A cell → the value, before its type's own reading: what spreadsheets do to it undone (a tax branch's zeros) */
  value?: (cell: unknown) => unknown
}

export interface FieldMeta {
  type?: FieldType
  /** i18n key; defaults to `<app>:<resource>.fields.<field>` */
  label?: string
  options?: readonly string[]
  /** i18n prefix for option labels shared by many resources (e.g. `doc.status`); default `<app>:<resource>.options.<field>` */
  optionLabels?: string
  tones?: Readonly<Record<string, Tone>>
  /** Relation: the value is the id of a record of another resource */
  ref?: RefMeta
  /** Free-text autocomplete: suggest values already used in this field */
  suggest?: boolean
  /** Included in the list's quick search */
  search?: boolean
  /** Offered as a list filter */
  filter?: boolean
  list?: false | ColumnMeta
  form?: false | FormFieldMeta
  import?: false | FieldImport
  export?: boolean
  /** Set by the server (document numbers, totals, timestamps): shown, never edited */
  readOnly?: boolean
  /**
   * `false`: left out of a duplicate (`ResourceConfig.duplicate`), as what makes the record itself: its own date, the
   * supplier's invoice number, a serial number. Files never carry over, being the evidence of one record
   */
  copy?: false
  /**
   * Warn in the form when another record already holds this value (a tax ID, a phone), linking to it before the same
   * partner is added twice; never refused, as two can share one. `with`: and the same values there where both have one
   * (a tax ID with its branch: another branch of one company is not the same partner). Phones match number by number
   */
  warnTaken?: true | { with: readonly string[] }
}

export interface ResolvedField {
  name: string
  type: FieldType
  label: string
  required: boolean
  options?: readonly string[]
  optionLabels?: string
  tones?: Readonly<Record<string, Tone>>
  ref?: RefMeta
  suggest: boolean
  search: boolean
  filter: boolean
  list: false | ColumnMeta
  form: false | FormFieldMeta
  import: false | FieldImport
  export: boolean
  readOnly: boolean
  /** Carried into a duplicate (`ResourceConfig.duplicate`): an input field, not a file, not `copy: false` */
  copy: boolean
  /** See `FieldMeta.warnTaken` */
  warnTaken?: { with: readonly string[] }
  /** The schema accepts `null`, so an emptied input can clear the stored value */
  nullable: boolean
  /** Schema default, used for new records */
  defaultValue?: unknown
  min?: number
  max?: number
  maxLength?: number
}

export interface ResourceCard {
  title: string
  subtitle?: string
  meta?: readonly string[]
  status?: string
  /** A field holding a person's photo (an uploaded file's id): their avatar beside the title */
  photo?: string
}

export interface ResourceConfig<S extends z.ZodObject = z.ZodObject> {
  /** Its app, or a shared module (`cash`) */
  app: ResourceHome
  name: string
  /**
   * Where its pages are in its app: `waybills` → /tms/waybills, /tms/waybills/new, /tms/waybills/:id (a shared module's
   * in each app that shows it). What links to a record from elsewhere goes there; none: no pages of its own
   */
  path?: string
  /** Input schema used for create; update uses `schema.partial()` */
  schema: S
  fields?: Readonly<Record<string, FieldMeta>>
  /** Field shown as the record's name (in pickers, breadcrumbs, toasts) */
  title: string
  /** List column order; defaults to every field with a list column */
  columns?: readonly string[]
  /** Form section order; fields without a section go to `main` */
  sections?: readonly string[]
  /** e.g. `-createdAt` or `pickupDate,pickupTime` */
  defaultSort?: string
  /** Permission actions this resource supports; defaults to the standard set */
  actions?: readonly string[]
  /** Field(s) compared with the user id for `@own` permissions; several = any of them (a transfer's sender or receiver) */
  ownerField?: string | readonly string[]
  /**
   * The fields that make a record one's own in a personal view (Thananan OS, `?mine=1`), when not
   * every owner field: a transfer is mine as its sender or receiver, not because I recorded it for someone
   */
  mineFields?: readonly string[]
  /** Fields holding the record's branch, compared with the user's home branch for `@branch` permissions */
  branchFields?: readonly string[]
  /** Field used to match existing rows when importing in update mode */
  importKey?: string
  /** Layout of the record on phones (list becomes cards) */
  card?: ResourceCard
  /**
   * A document with a draft → submitted → cancelled life (see `docstatus.ts`): adds the
   * `docstatus` fields and the submit, cancel and amend actions.
   */
  submittable?: boolean | SubmittableConfig
  /**
   * A record starts a new one (ทำสำเนา, as in Frappe): the form opens prefilled with the fields that `copy`, the rest as a
   * new record starts, and saves as any new record. Not for what a copy would settle twice (a receipt's waybills)
   */
  duplicate?: boolean
}

export interface Resource<S extends z.ZodObject = z.ZodObject> extends Omit<ResourceConfig<S>, 'submittable'> {
  /** `<app>.<name>`, also the permission subject */
  key: string
  actions: readonly string[]
  columns: readonly string[]
  sections: readonly string[]
  fieldList: readonly ResolvedField[]
  field(name: string): ResolvedField | undefined
  subjectInfo: SubjectInfo
  /** Set for documents with a draft → submitted → cancelled life */
  submittable?: Submittable
  /** The fields compared with the user in a personal view (`mineFields`, else every owner field); empty: none */
  mine: readonly string[]
}

/** Fields every stored record has, regardless of resource. */
export interface BaseRecord {
  id: string
  createdAt: string
  updatedAt: string
}

export type RecordOf<R extends Resource> = z.output<R['schema']> & BaseRecord & Record<string, unknown>
export type InputOf<R extends Resource> = z.input<R['schema']>

interface JsonSchemaProp {
  type?: string | string[]
  default?: unknown
  format?: string
  enum?: unknown[]
  items?: JsonSchemaProp
  anyOf?: JsonSchemaProp[]
  minimum?: number
  maximum?: number
  maxLength?: number
}

function isNullable(prop: JsonSchemaProp | undefined): boolean {
  if (!prop) return false
  if (Array.isArray(prop.type)) return prop.type.includes('null')
  return prop.anyOf?.some((p) => p.type === 'null') ?? false
}

function nonNull(prop: JsonSchemaProp): JsonSchemaProp {
  if (prop.anyOf) return prop.anyOf.find((p) => p.type !== 'null') ?? prop
  return prop
}

function inferType(raw: JsonSchemaProp): FieldType {
  const prop = nonNull(raw)
  const type = Array.isArray(prop.type) ? prop.type.find((t) => t !== 'null') : prop.type
  if (prop.enum) return 'select'
  switch (type) {
    case 'string':
      if (prop.format === 'date') return 'date'
      if (prop.format === 'date-time') return 'datetime'
      if (prop.format === 'email') return 'email'
      return 'text'
    case 'integer':
      return 'integer'
    case 'number':
      return 'number'
    case 'boolean':
      return 'boolean'
    case 'array': {
      const items = prop.items ? nonNull(prop.items) : undefined
      if (items?.enum) return 'multiselect'
      return items?.type === 'string' ? 'tags' : 'json'
    }
    default:
      return 'json'
  }
}

const SAFE = Number.MAX_SAFE_INTEGER

export function defineResource<S extends z.ZodObject>(config: ResourceConfig<S>): Resource<S> {
  const key = `${config.app}.${config.name}`
  const json = z.toJSONSchema(config.schema, { io: 'input', unrepresentable: 'any' }) as {
    properties?: Record<string, JsonSchemaProp>
    required?: string[]
  }
  const props = json.properties ?? {}
  const required = new Set(json.required ?? [])
  const submittable: Submittable | undefined = config.submittable
    ? { allowOnSubmit: 'all', cancelReason: 'optional', ...(config.submittable === true ? {} : config.submittable) }
    : undefined
  const meta: Readonly<Record<string, FieldMeta>> = submittable
    ? { ...config.fields, ...docFields, amendedFrom: { ...docFields.amendedFrom, type: 'ref', ref: { resource: key } } }
    : (config.fields ?? {})
  const names = [...Object.keys(props), ...Object.keys(meta).filter((n) => !(n in props))]

  const fieldList: ResolvedField[] = names.map((name) => {
    const prop = props[name] ? nonNull(props[name]) : undefined
    const m = meta[name] ?? {}
    const readOnly = m.readOnly ?? !prop
    const enumValues = prop?.enum?.filter((v): v is string => typeof v === 'string')
    const type = m.type ?? (prop ? inferType(props[name] as JsonSchemaProp) : 'text')
    return {
      name,
      type,
      label: m.label ?? `${config.app}:${config.name}.fields.${name}`,
      required: required.has(name),
      options: m.options ?? enumValues ?? prop?.items?.enum?.filter((v): v is string => typeof v === 'string'),
      optionLabels: m.optionLabels,
      tones: m.tones,
      ref: m.ref,
      suggest: m.suggest ?? false,
      search: m.search ?? false,
      filter: m.filter ?? false,
      list: m.list ?? (type === 'json' || type === 'textarea' ? false : {}),
      form: readOnly ? false : (m.form ?? {}),
      import: readOnly ? false : (m.import ?? {}),
      export: m.export ?? type !== 'json',
      readOnly,
      copy: !readOnly && type !== 'file' && m.copy !== false,
      warnTaken: m.warnTaken === true ? { with: [] } : m.warnTaken,
      nullable: isNullable(props[name]),
      defaultValue: props[name]?.default,
      min: prop?.minimum !== undefined && Math.abs(prop.minimum) < SAFE ? prop.minimum : undefined,
      max: prop?.maximum !== undefined && Math.abs(prop.maximum) < SAFE ? prop.maximum : undefined,
      maxLength: prop?.maxLength,
    }
  })

  const byName = new Map(fieldList.map((f) => [f.name, f]))
  for (const col of config.columns ?? []) {
    if (!byName.has(col)) throw new Error(`[${key}] column "${col}" is not a field`)
  }
  const owners = typeof config.ownerField === 'string' ? [config.ownerField] : (config.ownerField ?? [])
  const mine = config.mineFields ?? owners
  for (const ref of [config.title, ...owners, ...mine, config.importKey, ...(config.branchFields ?? [])].filter(Boolean) as string[]) {
    if (!byName.has(ref)) throw new Error(`[${key}] "${ref}" is not a field`)
  }
  for (const [name, m] of Object.entries(meta)) {
    if (m.copy === false && !config.duplicate) throw new Error(`[${key}] "${name}" says copy: false, but the resource has no duplicate`)
    for (const other of m.warnTaken === true ? [] : (m.warnTaken?.with ?? [])) {
      if (!byName.has(other)) throw new Error(`[${key}] warnTaken of "${name}" names "${other}", not a field`)
    }
  }

  const baseActions = config.actions ?? standardActions
  const actions = submittable ? [...baseActions, ...docActions.filter((a) => !baseActions.includes(a))] : baseActions
  const sections = config.sections ?? ['main']
  for (const name of submittable && submittable.allowOnSubmit !== 'all' ? submittable.allowOnSubmit : []) {
    if (!byName.get(name)?.form) throw new Error(`[${key}] allowOnSubmit "${name}" is not an editable field`)
  }
  // Lifecycle columns are there to switch on, after the resource's own
  const lifecycleColumns = submittable ? fieldList.filter((f) => f.name in docFields && f.list !== false).map((f) => f.name) : []
  const columns = config.columns
    ? [...config.columns, ...lifecycleColumns.filter((c) => !config.columns?.includes(c))]
    : fieldList.filter((f) => f.list !== false).map((f) => f.name)

  return {
    ...config,
    key,
    actions,
    sections,
    submittable,
    columns,
    fieldList,
    field: (name) => byName.get(name),
    mine,
    subjectInfo: { subject: key, app: config.app, actions, ownerField: config.ownerField, branchFields: config.branchFields },
  }
}

/** i18n key helpers, so every screen asks for labels the same way. */
export const labels = {
  title: (r: Pick<Resource, 'app' | 'name'>) => `${r.app}:${r.name}.title`,
  plural: (r: Pick<Resource, 'app' | 'name'>) => `${r.app}:${r.name}.plural`,
  section: (r: Pick<Resource, 'app' | 'name'>, section: string) => `${r.app}:${r.name}.sections.${section}`,
  option: (r: Pick<Resource, 'app' | 'name'> & Partial<Pick<Resource, 'field'>>, field: string, value: string) => {
    const shared = r.field?.(field)?.optionLabels
    return shared ? `${shared}.${value}` : `${r.app}:${r.name}.options.${field}.${value}`
  },
  action: (r: Pick<Resource, 'app' | 'name'>, action: string) => `${r.app}:${r.name}.actions.${action}`,
}

/** The fields a spreadsheet can fill, in the template's column order */
export function importFields(resource: Resource): ResolvedField[] {
  return resource.fieldList.filter((f) => f.import !== false && !f.readOnly && f.type !== 'json')
}

/** What a duplicate of the record starts from (`duplicate`): the fields that `copy` and hold a value */
export function copyOf(resource: Resource, record: Readonly<Record<string, unknown>>): Record<string, unknown> {
  return Object.fromEntries(resource.fieldList.filter((f) => f.copy && record[f.name] != null).map((f) => [f.name, record[f.name]]))
}

/** A field's column header in templates: its own for sheets, else its label (an i18n key either way) */
export const templateHeader = (f: ResolvedField) => (f.import ? f.import.header : undefined) ?? f.label
